// hooks/graphql/useProdStatData.ts
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { message } from 'antd';
import {
  VALIDATED_METER_READS,
  CORRUPTED_METER_READS,
  UPDATE_METER_DATA_BULK,
  BULK_APPROVAL_JOB_STATUS,
  MOVE_METERS_TO_APPROVAL,
  AVAILABLE_LOCATIONS,
  DECOMMISSION_METER,
} from 'graphql/prodStatDashboard.gql';
import type { MeterRow, SaveFeedback, AsyncApprovalFeedback } from '@custom-types/prodStat';
import useSession from '@hooks/useSession';
import { getRuntimeEnv } from '@utils/runtimeEnv';

type UseProdStatOptions = {
  type: 'validated' | 'corrupted';
  monthYear?: string;
};

export const useProdStat = ({ type, monthYear }: UseProdStatOptions) => {
  const QUERY = type === 'validated' ? VALIDATED_METER_READS : CORRUPTED_METER_READS;

  const [tableData, setTableData] = useState<MeterRow[]>([]);
  const [originalData, setOriginalData] = useState<MeterRow[]>([]);
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<MeterRow>>>({});
  const [saveFeedback, setSaveFeedback] = useState<SaveFeedback | null>(null);
  const [moveFeedback, setMoveFeedback] = useState<SaveFeedback | null>(null);
  // Separate banner for the async bulk-approval flow: yellow "in progress" → green
  // "completed" / red "failed". Persists until the user dismisses it.
  const [asyncApproval, setAsyncApproval] = useState<AsyncApprovalFeedback | null>(null);

  const [savedRowIds, setSavedRowIds] = useState<Set<number>>(new Set());
  const [savedRowSnapshots, setSavedRowSnapshots] = useState<Record<number, MeterRow>>({});

  // NEW: tracks which rows have been bulk-selected for approval
  const [bulkApprovalSelection, setBulkApprovalSelection] = useState<Set<number>>(new Set());

  const { isAdmin, isManager, isSuperAdmin, isUser, account } = useSession();
  const username = account?.name || account?.username || '';

  const { data, loading, refetch } = useQuery(QUERY, {
    variables: { monthYear },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
  });

  const { data: locationsData, loading: locationsLoading } = useQuery(AVAILABLE_LOCATIONS, {
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (data) {
      const rows: MeterRow[] =
        (type === 'validated' ? data.validatedMeterReads : data.corruptedMeterReads) ?? [];
      setTableData(rows);
      setOriginalData(rows);

      const alreadySaved = new Set<number>(
        rows
          .filter((r) => r.remediated === 'remediated' || r.remediated === 'ignored')
          .map((r) => r.meter_computed_id)
      );
      setSavedRowIds(alreadySaved);
      setSavedRowSnapshots({});
      setBulkApprovalSelection(new Set()); // clear on data reload
    }
  }, [data, type]);

  useEffect(() => {
    if (monthYear) {
      refetch({ monthYear });
    }
  }, [monthYear, refetch]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch({ monthYear });
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [monthYear, refetch]);

  const [moveMeters, { loading: moving, error: moveError }] = useMutation(MOVE_METERS_TO_APPROVAL);
  const [updateMeterDataBulk, { loading: saving }] = useMutation(UPDATE_METER_DATA_BULK);
  const [decommissionMeterMutation, { loading: decommissioning }] = useMutation(DECOMMISSION_METER);

  // ─── Async bulk-approval status polling (Step 0 — async wrapper) ──────────────
  // After a multi-meter batch is queued, poll the job status every 10s. On success,
  // flip exactly the submitted rows to "Approved" in place — no page refresh, so the
  // user's other in-progress edits aren't lost.
  const apolloClient = useApolloClient();
  const [pollJobId, setPollJobId] = useState<number | null>(null);
  const pollMeterIdsRef = useRef<number[]>([]);
  // Whether the in-flight job is an approval action vs. a pure "Ready for Approval"
  // status change — picks which banner copy and row-flip applies on completion.
  const pollIsApprovalRef = useRef<boolean>(true);

  useEffect(() => {
    if (pollJobId == null) return;
    let cancelled = false;

    const checkStatus = async () => {
      try {
        const { data: statusData } = await apolloClient.query({
          query: BULK_APPROVAL_JOB_STATUS,
          variables: { jobId: pollJobId },
          fetchPolicy: 'network-only',
        });
        if (cancelled) return;
        const job = statusData?.bulkApprovalJobStatus;
        if (!job) return;

        if (job.status === 'succeeded') {
          const isApprovalAction = pollIsApprovalRef.current;
          // job.approvedMeterComputedIds is only ever populated for approval actions
          // (the backend computes it from approval_status==='Approved' updates) — for
          // a pure Ready-for-Approval job it comes back empty, so only trust it on the
          // approval path; otherwise use what we submitted.
          const submittedIds: number[] = isApprovalAction
            ? (job.approvedMeterComputedIds ?? pollMeterIdsRef.current)
            : pollMeterIdsRef.current;

          if (isApprovalAction) {
            // Flip the submitted rows to fully-approved in place — matching what a
            // server refetch would return: approval_status AND ready_for_approval both
            // 'Approved', signature filled. Apply to originalData too so the row greys
            // out / becomes read-only exactly like a persisted approval.
            const applyApproval = (rows: MeterRow[]): MeterRow[] =>
              rows.map((row) =>
                submittedIds.includes(row.meter_computed_id)
                  ? {
                    ...row,
                    approval_status: 'Approved',
                    ready_for_approval: 'Approved',
                    prod_stat_approval_signature: row.prod_stat_approval_signature || username,
                  }
                  : row
              );
            setTableData(applyApproval);
            setOriginalData(applyApproval);
          } else {
            const applyReady = (rows: MeterRow[]): MeterRow[] =>
              rows.map((row) =>
                submittedIds.includes(row.meter_computed_id)
                  ? { ...row, ready_for_approval: 'Ready for Approval' }
                  : row
              );
            setTableData(applyReady);
            setOriginalData(applyReady);
          }

          setAsyncApproval({
            status: 'completed',
            message: isApprovalAction
              ? `Approval completed for ${submittedIds.length} meter${submittedIds.length === 1 ? '' : 's'}.`
              : `Status updated for ${submittedIds.length} meter${submittedIds.length === 1 ? '' : 's'}.`,
          });
          for (const agcErr of job.agcErrors ?? []) {
            message.warning(`AGC calculation failed for ${agcErr.meterName}: ${agcErr.error}`);
          }
          setPollJobId(null);
        } else if (job.status === 'failed') {
          setAsyncApproval({ status: 'failed', message: job.errorMessage || 'Bulk approval failed' });
          message.error(job.errorMessage || 'Bulk approval failed');
          setPollJobId(null);
        }
        // queued / processing → keep polling
      } catch (err) {
        // Transient error (network blip, etc.) — the next tick retries.
        console.error('Bulk approval status poll failed:', err);
      }
    };

    checkStatus(); // check once immediately, then on an interval
    const intervalId = setInterval(checkStatus, 10000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [pollJobId, apolloClient, username]);

  useEffect(() => {
    if (moveError?.graphQLErrors?.length) {
      const msg = moveError.graphQLErrors[0].message;
      setMoveFeedback({ status: 'error', message: msg });
      message.error(msg);
    }
  }, [moveError]);

  const handleEdit = (id: number, field: keyof MeterRow, value: unknown) => {
    setTableData((prev) =>
      prev.map((row) =>
        row.meter_computed_id === id ? ({ ...row, [field]: value } as MeterRow) : row
      )
    );
    setPendingChanges((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value } as Partial<MeterRow>,
    }));
  };

  const handleSaveRow = async (id: number, options?: { ignored?: boolean; markInactive?: boolean }) => {
    const currentRow = tableData.find((row) => row.meter_computed_id === id);
    if (!currentRow) {
      message.error('Row not found');
      return;
    }

    setSavedRowSnapshots((prev) => ({ ...prev, [id]: { ...currentRow } }));
    const rowChanges = pendingChanges[id] ?? {};
    const remediated = options?.ignored ? 'ignored' : 'remediated';
    const updatePayload = { meter_computed_id: id, ...rowChanges, remediated };

    setSaveFeedback(null);

    try {
      const { data: resp, errors } = await updateMeterDataBulk({
        variables: { updates: [updatePayload] },
      });

      if (resp?.updateMeterDataBulk?.approved) {
        setPendingChanges((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        setSavedRowIds((prev) => new Set(prev).add(id));
        setTableData((prev) =>
          prev.map((row) =>
            row.meter_computed_id === id
              ? { ...row, ...rowChanges, remediated, ...(options?.markInactive ? { isInactiveThisMonth: true } : {}) }
              : row
          )
        );
        setSaveFeedback({ status: 'success', message: 'Record saved successfully!' });
        message.success('Record saved successfully!');

        if (currentRow.meter_read_type?.toLowerCase() === 'new' && rowChanges.location) {
          message.info('New meter location updated. Orphan entry created/updated for tracking.', 5);
        }

        for (const agcErr of resp.updateMeterDataBulk.agcErrors ?? []) {
          message.warning(`AGC calculation failed for ${agcErr.meterName}: ${agcErr.error}`);
        }
      } else {
        const msg = errors?.[0]?.message || 'Failed to save record';
        setSaveFeedback({ status: 'error', message: msg });
        message.error(msg);
      }
    } catch (err: any) {
      setSaveFeedback({ status: 'error', message: err.message || 'Failed to save record' });
      message.error(err.message || 'Failed to save record');
    }
  };

  const handleUndoRow = async (id: number) => {
    setSaveFeedback(null);
    try {
      const { data: resp, errors } = await updateMeterDataBulk({
        variables: {
          updates: [{ meter_computed_id: id, remediated: 'UNDO_REMEDIATED' }],
        },
      });

      if (resp?.updateMeterDataBulk?.approved) {
        setSavedRowIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });

        const snapshot = savedRowSnapshots[id];
        if (snapshot) {
          setTableData((prev) =>
            prev.map((row) =>
              row.meter_computed_id === id
                ? {
                  ...snapshot,
                  remediated:
                    snapshot.remediated === 'remediated' ? 'no' : snapshot.remediated,
                }
                : row
            )
          );
          setSavedRowSnapshots((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        } else {
          await refetch();
        }

        setSaveFeedback({ status: 'success', message: 'Undo successful.' });
        message.success('Undo successful. Row returned to Prod Stat Prep.');
      } else {
        const msg = errors?.[0]?.message || 'Failed to undo.';
        setSaveFeedback({ status: 'error', message: msg });
        message.error(msg);
      }
    } catch (err: any) {
      setSaveFeedback({ status: 'error', message: err.message || 'Failed to undo.' });
      message.error(err.message || 'Failed to undo.');
    }
  };

  const isUndoDisabled = (row: MeterRow): boolean => {
    if (row.meter_read_type?.toLowerCase() === 'new' && row.remediated !== 'ignored') return true;
    const serverRow = originalData.find((r) => r.meter_computed_id === row.meter_computed_id);
    if ((serverRow ?? row).approval_status === 'Approved') return true;
    return false;
  };

  const handleMoveMeters = async () => {
    const idsToMove = tableData
      .filter((row) => row.meter_read_type === 'Actual' || row.remediated === 'yes')
      .map((row) => row.meter_computed_id);

    if (!idsToMove.length) {
      message.info('No meters eligible to move.');
      return;
    }
    setMoveFeedback(null);
    try {
      const { data } = await moveMeters({ variables: { ids: idsToMove } });
      if (data.moveMetersToApproval) {
        setMoveFeedback({
          status: 'success',
          message: `${idsToMove.length} meters moved successfully!`,
        });
        message.success(`${idsToMove.length} meters moved successfully!`);
        await refetch();
      }
    } catch (err: any) {
      setMoveFeedback({ status: 'error', message: err.message || 'Failed to move meters' });
      message.error(err.message || 'Failed to move meters');
    }
  };

  const handleSave = async () => {
    const updates = Object.keys(pendingChanges).map((id) => {
      const change = pendingChanges[id];
      const row = tableData.find((r) => r.meter_computed_id === Number(id));
      const update: Record<string, unknown> = { meter_computed_id: Number(id), ...change };
      if (change.approval_status === 'Approved' && row) {
        update.dollar_value = row.dollar_value ?? null;
        console.log(`[FE dollar_value] meter_computed_id=${id} row.dollar_value=${row.dollar_value} sending=${update.dollar_value}`);
      }
      return update;
    });

    if (!updates.length) {
      message.info('No changes to save.');
      return;
    }

    setSaveFeedback(null);
    setAsyncApproval(null);

    // Step 0 — async wrapper: only route through processAsync when the batch (a)
    // actually contains an approval action and (b) is large enough that the
    // synchronous path risks the original timeout. Below the threshold — and any
    // batch that's purely "Ready for Approval" with no approvals at all — was never
    // the slow case (that heavy per-meter calculation only runs on approval), so
    // those always take the synchronous path below, same as before this feature
    // existed. Single-item saves (including handleSaveRow/handleUndoRow, which never
    // reach this function) always take the synchronous path too.
    const ASYNC_BATCH_THRESHOLD = 15;
    const asyncEnabled = getRuntimeEnv('REACT_APP_BULK_APPROVAL_ASYNC_ENABLED') === 'true';
    const hasApprovals = updates.some((u) => u.approval_status === 'Approved');
    const processAsync = asyncEnabled && hasApprovals && updates.length > ASYNC_BATCH_THRESHOLD;
    // Only include processAsync in the call when true, so the synchronous path's
    // request is byte-for-byte identical to before this change, not just functionally
    // equivalent — matches the "wrap, don't rewrite" approach used on the backend too.
    const variables = processAsync ? { updates, processAsync } : { updates };

    // The processing/completed banner is reserved for batches actually large enough
    // to matter (over the same 15-meter threshold as the async gate) — a batch that
    // size is a real approval save above the threshold (async, handled below) or a
    // large synchronous Ready-for-Approval save. Anything at or under 15 keeps the
    // original plain "Saved successfully!" feedback — it resolves fast enough that
    // flashing a processing banner first would just be noise.
    const showStatusBanner = updates.length > ASYNC_BATCH_THRESHOLD;

    if (!processAsync && showStatusBanner) {
      setAsyncApproval({
        status: 'processing',
        message: hasApprovals
          ? 'Approval request is being processed. This can take up to a minute'
          : 'Status change request is being processed. This can take up to a minute',
      });
    }

    try {
      const { data } = await updateMeterDataBulk({ variables });
      if (data.updateMeterDataBulk?.queued) {
        const jobId = data.updateMeterDataBulk.jobId as number;
        // The meter_computed_ids this banner/row-flip tracks once the job finishes —
        // the approved subset for an approval action, or every submitted meter for a
        // pure Ready-for-Approval action (there's no approval_status subset to filter
        // to in that case).
        const submittedIds = hasApprovals
          ? updates.filter((u) => u.approval_status === 'Approved').map((u) => Number(u.meter_computed_id))
          : updates.map((u) => Number(u.meter_computed_id));

        setPendingChanges({});
        setBulkApprovalSelection(new Set());

        // While the background job runs, show these rows as "Ready for Approval"
        // (not "Approved") — the sync path wouldn't show them approved until it
        // actually finished either.
        const originalById = new Map(originalData.map((r) => [r.meter_computed_id, r]));
        setTableData((prev) =>
          prev.map((row) =>
            submittedIds.includes(row.meter_computed_id)
              ? {
                ...(originalById.get(row.meter_computed_id) ?? row),
                ready_for_approval: 'Ready for Approval',
              }
              : row
          )
        );

        setAsyncApproval({
          status: 'processing',
          message: hasApprovals
            ? 'Approval request is being processed. This can take up to a minute'
            : 'Status change request is being processed. This can take up to a minute',
        });
        pollMeterIdsRef.current = submittedIds;
        pollIsApprovalRef.current = hasApprovals;
        setPollJobId(jobId);
        return;
      }
      if (data.updateMeterDataBulk?.approved) {
        setPendingChanges({});
        await refetch();
        if (showStatusBanner) {
          setAsyncApproval({
            status: 'completed',
            message: hasApprovals
              ? `Approval completed for ${updates.length} meter${updates.length === 1 ? '' : 's'}.`
              : `Status updated for ${updates.length} meter${updates.length === 1 ? '' : 's'}.`,
          });
        } else {
          setSaveFeedback({
            status: 'success',
            message: `Saved ${updates.length} updates successfully!`,
          });
        }
        message.success(`Saved ${updates.length} updates successfully!`);
        for (const agcErr of data.updateMeterDataBulk.agcErrors ?? []) {
          message.warning(`AGC calculation failed for ${agcErr.meterName}: ${agcErr.error}`);
        }
      } else if (showStatusBanner) {
        setAsyncApproval({ status: 'failed', message: 'Failed to save updates' });
        message.error('Failed to save updates');
      } else {
        setSaveFeedback({ status: 'error', message: 'Failed to save updates' });
        message.error('Failed to save updates');
      }
    } catch (err: any) {
      if (showStatusBanner) {
        setAsyncApproval({ status: 'failed', message: err.message || 'Failed to save updates' });
      } else {
        setSaveFeedback({ status: 'error', message: err.message || 'Failed to save updates' });
      }
      message.error(err.message || 'Failed to save updates');
    }
  };

  // ─── NEW: Bulk approval helpers ──────────────────────────────────────────────

  /**
   * Rows eligible for bulk approval:
   *   - ready_for_approval === 'Ready for Approval'
   *   - not yet approved on the server (originalData check)
   */
  const bulkApprovableIds = (data: MeterRow[]): number[] =>
    data
      .filter((row) => {
        // Must be ready for approval
        if (row.ready_for_approval !== 'Ready for Approval') return false;

        const originalRow = originalData.find(r => r.meter_computed_id === row.meter_computed_id);
        if (originalRow?.approval_status === 'Approved') return false;

        return true;
      })
      .map((row) => row.meter_computed_id);

  /**
   * True when the pending changes include at least one new approval — i.e.
   * this save would approve a meter that is not yet approved on the server —
   * AND no meter is already approved on the server for this month (first approval only).
   */
  const anyAlreadyApprovedOnServer = originalData.some((r) =>
    r.approval_status?.match(/^Approved$/i)
  );

  const hasPendingApprovals = !anyAlreadyApprovedOnServer &&
    Object.entries(pendingChanges).some(([id, changes]) => {
      if (!changes.approval_status?.match(/^Approved$/i)) return false;
      const original = originalData.find((r) => r.meter_computed_id === Number(id));
      return !original?.approval_status?.match(/^Approved$/i);
    });

  /**
   * True when any pending change belongs to a row that is already approved
   * on the server — saving that would be invalid.
   */
  const hasPendingChangesOnApprovedRows = Object.keys(pendingChanges).some((id) =>
    originalData.find((r) => r.meter_computed_id === Number(id))
      ?.approval_status?.match(/^Approved$/i)
  );

  /** Save should be blocked when there are changes staged for already-approved rows. */
  const isSaveDisabled = hasPendingChangesOnApprovedRows || Object.keys(pendingChanges).length === 0;

  /**
   * Derived checkbox state for the header:
   *   - "checked"       : all approvable rows are selected
   *   - "indeterminate" : some (but not all) are selected
   *   - neither         : none selected
   *   - "disabled"      : all eligible rows are already approved on the server
   */
  const getBulkApproveHeaderState = () => {
    const eligible = bulkApprovableIds(tableData);
    const allAlreadyApproved =
      tableData.length > 0 &&
      tableData.every((r) =>
        originalData.find((o) => o.meter_computed_id === r.meter_computed_id)
          ?.approval_status?.match(/^Approved$/i)
      );
    if (eligible.length === 0) return { checked: false, indeterminate: false, disabled: allAlreadyApproved };
    const selectedCount = eligible.filter((id) => bulkApprovalSelection.has(id)).length;
    return {
      checked: selectedCount === eligible.length,
      indeterminate: selectedCount > 0 && selectedCount < eligible.length,
      disabled: false,
    };
  };

  /**
   * Toggle all-or-none for the header checkbox.
   * Only callable by an approver (isAdmin || isManager).
   */
  const handleBulkApproveHeaderChange = (checked: boolean) => {
    const eligible = bulkApprovableIds(tableData);
    if (checked) {
      // Select all eligible rows AND immediately apply approval to local state
      setBulkApprovalSelection(new Set(eligible));
      setTableData((prev) =>
        prev.map((row) =>
          eligible.includes(row.meter_computed_id)
            ? {
              ...row,
              approval_status: 'Approved',
              prod_stat_approval_signature: row.prod_stat_approval_signature || username,
            }
            : row
        )
      );
      const signatureByEligibleId = new Map(
        tableData
          .filter((r) => eligible.includes(r.meter_computed_id))
          .map((r) => [r.meter_computed_id, r.prod_stat_approval_signature || username])
      );
      setPendingChanges((prev) => {
        const next = { ...prev };
        eligible.forEach((id) => {
          next[id] = {
            ...next[id],
            approval_status: 'Approved',
            prod_stat_approval_signature: signatureByEligibleId.get(id) ?? username,
          };
        });
        return next;
      });
    } else {
      setBulkApprovalSelection(new Set());
      const originalById = new Map(originalData.map((r) => [r.meter_computed_id, r]));
      setTableData((prev) =>
        prev.map((row) => {
          if (!eligible.includes(row.meter_computed_id)) return row;
          const original = originalById.get(row.meter_computed_id);
          if (original?.approval_status === 'Approved') return row;
          return original
            ? { ...row, approval_status: original.approval_status, prod_stat_approval_signature: original.prod_stat_approval_signature }
            : row;
        })
      );
      // Remove staged pending changes for deselected rows
      setPendingChanges((prev) => {
        const next = { ...prev };
        eligible.forEach((id) => {
          if (next[id]) {
            delete next[id].approval_status;
            delete next[id].prod_stat_approval_signature;
            if (Object.keys(next[id]).length === 0) delete next[id];
          }
        });
        return next;
      });
    }
  };

  // ─── Bulk "Ready for Approval" helpers (preparer) ────────────────────────────

  /** Rows eligible for bulk ready: not yet approved, not already 'Ready for Approval' */
  // All non-persisted rows — eligible for both check and uncheck
  const bulkReadyEligibleIds = (rows: MeterRow[]): number[] =>
    rows
      .filter((row) => !isRowOriginallyApproved(row.meter_computed_id))
      .map((row) => row.meter_computed_id);

  const getBulkReadyHeaderState = () => {
    const eligible = bulkReadyEligibleIds(tableData);
    if (eligible.length === 0) return { checked: false, indeterminate: false, disabled: true };
    const readyCount = tableData.filter(
      (r) => eligible.includes(r.meter_computed_id) && r.ready_for_approval === 'Ready for Approval'
    ).length;
    return {
      checked: readyCount === eligible.length,
      indeterminate: readyCount > 0 && readyCount < eligible.length,
      disabled: false,
    };
  };

  const handleBulkReadyHeaderChange = (checked: boolean) => {
    const eligible = bulkReadyEligibleIds(tableData);
    setTableData((prev) =>
      prev.map((row) =>
        eligible.includes(row.meter_computed_id)
          ? { ...row, ready_for_approval: checked ? 'Ready for Approval' : 'In Progress' }
          : row
      )
    );
    setPendingChanges((prev) => {
      const next = { ...prev };
      eligible.forEach((id) => {
        next[id] = { ...next[id], ready_for_approval: checked ? 'Ready for Approval' : 'In Progress' };
      });
      return next;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────────

  const getAvailableLocationsForRow = (currentLocation: string | null) => {
    if (!locationsData?.availableLocations) return [];
    return locationsData.availableLocations.filter(
      (location: string) => location !== currentLocation
    );
  };

  const hasPartialMeterLocationChange = (id: number) => {
    const changes = pendingChanges[id];
    const currentRow = tableData.find((row) => row.meter_computed_id === id);
    return (
      currentRow?.meter_read_type?.toLowerCase() === 'new' &&
      changes?.location &&
      changes.location !== currentRow.location
    );
  };

  const isRowOriginallyApproved = (id: number): boolean => {
    const originalRow = originalData.find((row) => row.meter_computed_id === id);
    return originalRow?.approval_status === 'Approved';
  };

  const handleDecommission = async (meterId: number) => {
    try {
      const { data: resp } = await decommissionMeterMutation({ variables: { meterId } });
      if (resp?.decommissionMeter) {
        message.success('Meter marked as Inactive.');
        await refetch();
      } else {
        message.error('Failed to mark meter as Inactive.');
      }
    } catch (err: any) {
      message.error(err.message || 'Failed to mark meter as Inactive.');
    }
  };

  return {
    data: tableData,
    loading,
    isAdmin,
    isManager,
    isSuperAdmin,
    isUser,
    saving,
    moving,
    saveFeedback,
    setSaveFeedback,
    asyncApproval,
    setAsyncApproval,
    moveFeedback,
    setMoveFeedback,
    savedRowIds,
    isUndoDisabled,
    availableLocations: locationsData?.availableLocations || [],
    locationsLoading,
    getAvailableLocationsForRow,
    hasPartialMeterLocationChange,
    isRowOriginallyApproved,
    handleEdit,
    handleSave,
    handleSaveRow,
    handleUndoRow,
    handleMoveMeters,
    handleDecommission,
    decommissioning,
    refetch,
    username,
    getBulkApproveHeaderState,
    handleBulkApproveHeaderChange,
    bulkApprovalSelection,
    hasPendingApprovals,
    isSaveDisabled,
    getBulkReadyHeaderState,
    handleBulkReadyHeaderChange,
  };
};